export type MovementType = "in" | "out";

export interface StockMovement {
  id: string;
  type: MovementType;
  docName: string;
  quantity: number;
  date: string | null;
  operator: string;
  warehouse: string;
  area: string;
  location: string;
  locationKey: string;
  note?: string | null;
}

export interface LocationStock {
  key: string;
  warehouse: string;
  area: string;
  location: string;
  in: number;
  out: number;
  stock: number;
}

export interface StockGoods {
  id: string;
  name: string;
  itemName: string;
  unit: string;
  inventoryLowerLimit: number | null;
  stock: number;
  in: number;
  out: number;
  locations: LocationStock[];
  movements: StockMovement[];
}

const EMPTY = "—";

function resolveLocation(storageLocation: any) {
  const area = storageLocation?.warehouseArea;
  const warehouse = area?.warehouse;

  return {
    key: storageLocation?.id ?? EMPTY,
    warehouse: warehouse?.name ?? EMPTY,
    area: area?.name ?? EMPTY,
    location: storageLocation?.name ?? EMPTY,
  };
}

function ensureEntry(map: Map<string, LocationStock>, storageLocation: any) {
  const resolved = resolveLocation(storageLocation);
  let entry = map.get(resolved.key);

  if (!entry) {
    entry = { ...resolved, in: 0, out: 0, stock: 0 };
    map.set(resolved.key, entry);
  }

  return entry;
}

function toMovement(
  record: any,
  doc: any,
  type: MovementType
): StockMovement | null {
  if (!record) return null;

  const resolved = resolveLocation(record.storageLocation);

  return {
    id: record.id,
    type,
    docName: doc?.name ?? EMPTY,
    quantity: Number(record.amount ?? 0),
    date: doc?.createdAt ?? null,
    operator: doc?.deliveryPerson || EMPTY,
    warehouse: resolved.warehouse,
    area: resolved.area,
    location: resolved.location,
    locationKey: resolved.key,
    note: doc?.remarks ?? null,
  };
}

export function buildStockGoods(products: any[]): StockGoods[] {
  return (products ?? []).map((product: any) => {
    const locationMap = new Map<string, LocationStock>();

    (product.productInventory ?? []).forEach((inventory: any) => {
      const entry = ensureEntry(locationMap, inventory.storageLocation);
      entry.stock += Number(inventory.amount ?? 0);
    });

    const receipts: StockMovement[] = (product.stockReceiptProduct ?? [])
      .map((record: any) => toMovement(record, record.stockReceipt, "in"))
      .filter(Boolean) as StockMovement[];

    const requests: StockMovement[] = (product.stockRequestProduct ?? [])
      .map((record: any) => toMovement(record, record.stockRequest, "out"))
      .filter(Boolean) as StockMovement[];

    (product.stockReceiptProduct ?? []).forEach((record: any) => {
      const entry = ensureEntry(locationMap, record.storageLocation);
      entry.in += Number(record.amount ?? 0);
    });

    (product.stockRequestProduct ?? []).forEach((record: any) => {
      const entry = ensureEntry(locationMap, record.storageLocation);
      entry.out += Number(record.amount ?? 0);
    });

    const hasInventory = (product.productInventory ?? []).length > 0;

    const locations = Array.from(locationMap.values()).map((entry) => ({
      ...entry,
      stock: hasInventory ? entry.stock : entry.in - entry.out,
    }));

    locations.sort((a, b) => b.stock - a.stock);

    const movements = [...receipts, ...requests].sort((a, b) => {
      const left = a.date ? new Date(a.date).getTime() : 0;
      const right = b.date ? new Date(b.date).getTime() : 0;
      return right - left;
    });

    const totalIn = receipts.reduce((sum, item) => sum + item.quantity, 0);
    const totalOut = requests.reduce((sum, item) => sum + item.quantity, 0);

    return {
      id: product.id,
      name: product.name ?? EMPTY,
      itemName: product.item?.name ?? product.defaultItem?.name ?? "",
      unit: product.unit ?? "",
      inventoryLowerLimit: product.inventoryLowerLimit ?? null,
      stock: hasInventory
        ? locations.reduce((sum, entry) => sum + entry.stock, 0)
        : totalIn - totalOut,
      in: totalIn,
      out: totalOut,
      locations,
      movements,
    };
  });
}

export function sortStockGoods(goods: StockGoods[]): StockGoods[] {
  return [...goods].sort(
    (a, b) => (a.stock > 0 ? 0 : 1) - (b.stock > 0 ? 0 : 1)
  );
}
